import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Paper,
  Box,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  MenuItem,
  Typography,
  Divider,
  Alert,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormStepper from './FormStepper';
import PricingTable from './PricingTable';
import Summary from './Summary';
import { 
  registrationData, 
  getRegistrationPeriod, 
  indianDelegateTypes, 
  internationalDelegateTypes 
} from '../data/registrationData';

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  nationality: Yup.string().required('Nationality is required'),
  delegateType: Yup.string().required('Delegate type is required'),
  conferenceType: Yup.string().required('Conference type is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  country: Yup.string().required('Country is required'),
  postalCode: Yup.string().required('Postal code is required'),
  institution: Yup.string().required('Institution is required'),
});

const RegistrationForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      nationality: 'indian',
      delegateType: '',
      conferenceType: 'mainConference',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      institution: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        console.log('Form submitted:', values);
        
        // Send data to the API
        const response = await fetch('https://zinvos.tessst.com/api/wonca-spot', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message || `API error: ${response.status}`
          );
        }
        
        const data = await response.json();
        console.log('API response:', data);
        
        // Navigate to thank you page on success
        navigate('/thank-you');
      } catch (error) {
        console.error('Submission error:', error);
        setSubmitError(error.message || 'Failed to submit registration. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleNext = () => {
    if (activeStep === 0) {
      if (formik.values.nationality && formik.values.delegateType && formik.values.conferenceType) {
        setActiveStep(1);
      } else {
        formik.setTouched({
          nationality: true,
          delegateType: true,
          conferenceType: true
        });
      }
    } else if (activeStep === 1) {
      const fieldsToValidate = ['firstName', 'lastName', 'email', 'phone', 'institution'];
      const errors = {};
      
      fieldsToValidate.forEach(field => {
        try {
          validationSchema.validateSyncAt(field, formik.values);
        } catch (error) {
          errors[field] = true;
        }
      });
      
      if (Object.keys(errors).length === 0) {
        setActiveStep(2);
      } else {
        formik.setTouched({
          ...formik.touched,
          ...fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {})
        });
      }
    } else if (activeStep === 2) {
      const fieldsToValidate = ['address', 'city', 'state', 'country', 'postalCode'];
      const errors = {};
      
      fieldsToValidate.forEach(field => {
        try {
          validationSchema.validateSyncAt(field, formik.values);
        } catch (error) {
          errors[field] = true;
        }
      });
      
      if (Object.keys(errors).length === 0) {
        setActiveStep(3);
      } else {
        formik.setTouched({
          ...formik.touched,
          ...fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {})
        });
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const currentPeriod = getRegistrationPeriod();
  const delegateTypes = formik.values.nationality === 'indian' 
    ? indianDelegateTypes 
    : internationalDelegateTypes;

  // Get step icon based on active step
  const getStepIcon = (step) => {
    switch(step) {
      case 0: return <EventIcon fontSize="large" color="primary" />;
      case 1: return <PersonIcon fontSize="large" color="primary" />;
      case 2: return <HomeIcon fontSize="large" color="primary" />;
      case 3: return <CheckCircleIcon fontSize="large" color="primary" />;
      default: return null;
    }
  };

  // Get step title based on active step
  const getStepTitle = (step) => {
    switch(step) {
      case 0: return "Delegate Information";
      case 1: return "Personal Details";
      case 2: return "Address Information";
      case 3: return "Review & Submit";
      default: return "";
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Fade in={activeStep === 0} timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {getStepIcon(activeStep)}
                <Typography variant="h5" sx={{ ml: 2, fontWeight: 'medium' }}>
                  {getStepTitle(activeStep)}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <FormControl component="fieldset" fullWidth margin="normal">
                    <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      Nationality
                    </FormLabel>
                    <RadioGroup
                      row
                      name="nationality"
                      value={formik.values.nationality}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel 
                        value="indian" 
                        control={<Radio color="primary" />} 
                        label="Indian" 
                        sx={{ '& .MuiFormControlLabel-label': { fontWeight: formik.values.nationality === 'indian' ? 'bold' : 'normal' } }}
                      />
                      <FormControlLabel 
                        value="international" 
                        control={<Radio color="primary" />} 
                        label="International" 
                        sx={{ '& .MuiFormControlLabel-label': { fontWeight: formik.values.nationality === 'international' ? 'bold' : 'normal' } }}
                      />
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <FormControl component="fieldset" fullWidth margin="normal">
                    <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      Conference Type
                    </FormLabel>
                    <RadioGroup
                      row={!isMobile}
                      name="conferenceType"
                      value={formik.values.conferenceType}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel 
                        value="mainConference" 
                        control={<Radio color="primary" />} 
                        label="Main Conference (5th & 6th April, 2025)" 
                        sx={{ 
                          '& .MuiFormControlLabel-label': { 
                            fontWeight: formik.values.conferenceType === 'mainConference' ? 'bold' : 'normal' 
                          } 
                        }}
                      />
                      <FormControlLabel 
                        value="fullConference" 
                        control={<Radio color="primary" />} 
                        label="Full Conference (4th to 6th April, 2025)" 
                        sx={{ 
                          '& .MuiFormControlLabel-label': { 
                            fontWeight: formik.values.conferenceType === 'fullConference' ? 'bold' : 'normal' 
                          } 
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <FormControl fullWidth margin="normal">
                    <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                      Delegate Type
                    </FormLabel>
                    <TextField
                      select
                      name="delegateType"
                      value={formik.values.delegateType}
                      onChange={formik.handleChange}
                      error={formik.touched.delegateType && Boolean(formik.errors.delegateType)}
                      helperText={formik.touched.delegateType && formik.errors.delegateType}
                      fullWidth
                      variant="outlined"
                      sx={{ '& .MuiSelect-select': { py: 1.5 } }}
                    >
                      {delegateTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </CardContent>
              </Card>
              
              <Alert 
                severity="info" 
                variant="filled"
                sx={{ 
                  mt: 2, 
                  mb: 3,
                  fontWeight: 'medium',
                  '& .MuiAlert-icon': { alignItems: 'center' }
                }}
              >
                Current registration period: Onsite Registration (After 30th March 2025)
              </Alert>
              
              <PricingTable 
                nationality={formik.values.nationality} 
                conferenceType={formik.values.conferenceType} 
              />
            </Box>
          </Fade>
        );
      
      case 1:
        return (
          <Fade in={activeStep === 1} timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {getStepIcon(activeStep)}
                <Typography variant="h5" sx={{ ml: 2, fontWeight: 'medium' }}>
                  {getStepTitle(activeStep)}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Name Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="firstName"
                        name="firstName"
                        label="First Name"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                        helperText={formik.touched.firstName && formik.errors.firstName}
                        margin="normal"
                        variant="outlined"
                        sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="lastName"
                        name="lastName"
                        label="Last Name"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                        helperText={formik.touched.lastName && formik.errors.lastName}
                        margin="normal"
                        variant="outlined"
                        sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Contact Information
                  </Typography>
                  
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    margin="normal"
                    variant="outlined"
                    sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                  />
                  
                  <TextField
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Phone Number"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                    margin="normal"
                    variant="outlined"
                    sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                  />
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Professional Information
                  </Typography>
                  
                  <TextField
                    fullWidth
                    id="institution"
                    name="institution"
                    label="Institution/Organization"
                    value={formik.values.institution}
                    onChange={formik.handleChange}
                    error={formik.touched.institution && Boolean(formik.errors.institution)}
                    helperText={formik.touched.institution && formik.errors.institution}
                    margin="normal"
                    variant="outlined"
                    sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                  />
                </CardContent>
              </Card>
            </Box>
          </Fade>
        );
      
      case 2:
        return (
          <Fade in={activeStep === 2} timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {getStepIcon(activeStep)}
                <Typography variant="h5" sx={{ ml: 2, fontWeight: 'medium' }}>
                  {getStepTitle(activeStep)}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Street Address
                  </Typography>
                  
                  <TextField
                    fullWidth
                    id="address"
                    name="address"
                    label="Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={formik.touched.address && formik.errors.address}
                    margin="normal"
                    multiline
                    rows={2}
                    variant="outlined"
                  />
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    City & State
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="city"
                        name="city"
                        label="City"
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        error={formik.touched.city && Boolean(formik.errors.city)}
                        helperText={formik.touched.city && formik.errors.city}
                        margin="normal"
                        variant="outlined"
                        sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="state"
                        name="state"
                        label="State/Province"
                        value={formik.values.state}
                        onChange={formik.handleChange}
                        error={formik.touched.state && Boolean(formik.errors.state)}
                        helperText={formik.touched.state && formik.errors.state}
                        margin="normal"
                        variant="outlined"
                        sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Country & Postal Code
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="country"
                        name="country"
                        label="Country"
                        value={formik.values.country}
                        onChange={formik.handleChange}
                        error={formik.touched.country && Boolean(formik.errors.country)}
                        helperText={formik.touched.country && formik.errors.country}
                        margin="normal"
                        variant="outlined"
                        sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="postalCode"
                        name="postalCode"
                        label="Postal Code"
                        value={formik.values.postalCode}
                        onChange={formik.handleChange}
                        error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
                        helperText={formik.touched.postalCode && formik.errors.postalCode}
                        margin="normal"
                        variant="outlined"
                        sx={{ '& .MuiInputBase-input': { py: 1.5 } }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        );
      
      case 3:
        return (
          <Fade in={activeStep === 3} timeout={500}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {getStepIcon(activeStep)}
                <Typography variant="h5" sx={{ ml: 2, fontWeight: 'medium' }}>
                  {getStepTitle(activeStep)}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              {submitError && (
                <Alert 
                  severity="error" 
                  variant="filled"
                  sx={{ mb: 3 }}
                >
                  {submitError}
                </Alert>
              )}
              
              <Summary 
                formData={formik.values} 
                onEdit={() => setActiveStep(0)} 
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            </Box>
          </Fade>
        );
      
      default:
        return null;
    }
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: { xs: 2, sm: 3 }, 
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}
    >
      <FormStepper activeStep={activeStep} />
      
      <form onSubmit={formik.handleSubmit}>
        {renderStepContent()}
        
        {activeStep < 3 && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 4,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              size="large"
              sx={{ 
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'medium'
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              size="large"
              sx={{ 
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'medium',
                boxShadow: 2
              }}
            >
              {activeStep === 2 ? 'Review' : 'Next'}
            </Button>
          </Box>
        )}
      </form>
    </Paper>
  );
};

export default RegistrationForm; 